import type { Triangulation, Vec2 } from '../../geo/types';
import type { CameraPose } from '../../data/types';
import type { CornerProbe } from './corners';
import { refineLabelBoundaries } from './labelRefine';
import { withoutDegenerates } from './degenerate';
import { createPainterBend, type PainterBend } from './painterBend';
import type { TrianglePlans } from './trianglePlans';

// The rasteriser draws an edge straight between its moved ends; where the painter view bends it more
// than this, the kink shows.
export const MAX_BEND_PX = 1;

export interface VertexLabeller {
  // Probes step this far from a vertex into the triangle that leaves them the most room.
  readonly reach: number;
  labelAt(vertex: Vec2, probe: CornerProbe): string | null;
}

export interface LabelBoundaries {
  readonly labeller: VertexLabeller;
  // Triangles are split until no edge between two different labels is longer.
  readonly minEdge: number;
}

export interface RefinementZone {
  // The longest an edge between these two points may be; Infinity leaves it to the other rules. It must
  // depend on the edge alone, so both triangles on an edge split it alike.
  longestEdge(a: Vec2, b: Vec2): number;
}

export interface SurfaceRefinement {
  readonly tileSize: number;
  // A renderer may keep quantized outline vertices on the shared edge's interpolation plane.
  withVertexHeight?(heightAt: (p: Vec2) => number): SurfaceRefinement;
  needsRefinement(a: Vec2, b: Vec2, c: Vec2): boolean;
}

export interface DensifyConfig {
  readonly triangulations?: TrianglePlans | undefined;
  // Edges are split until the painter view bends none of them more than MAX_BEND_PX.
  readonly bend?: PainterBend;
  readonly boundaries?: LabelBoundaries;
  readonly zone?: RefinementZone;
  readonly surface?: SurfaceRefinement;
}

function beyondZone(p1: Vec2, p2: Vec2, zone: RefinementZone | undefined): boolean {
  return zone !== undefined && Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) > zone.longestEdge(p1, p2);
}

export function shouldSplitEdge(p1: Vec2, p2: Vec2, config: DensifyConfig): boolean {
  if (beyondZone(p1, p2, config.zone)) return true;
  return config.bend !== undefined && config.bend.bendPx(p1, p2) > MAX_BEND_PX;
}

// Refinement for the painter view at `painterView` of geometry on the plane at `planeY`.
export function createDensifyConfig(painterView: CameraPose, planeY: number): DensifyConfig {
  return { bend: createPainterBend(painterView, planeY, MAX_BEND_PX) };
}

type TriangleIndices = readonly [number, number, number];
type EdgeSplits = readonly [boolean, boolean, boolean];
type MidpointOf = (ia: number, ib: number) => number;
type SplitsEdge = (ia: number, ib: number) => boolean;

// Children replace their triangle in place, so reading the leaves depth-first keeps the output order
// of a flat list rewritten every pass.
interface RefineNode {
  readonly tri: TriangleIndices;
  readonly children: RefineNode[];
}

const MAX_REFINE_PASSES = 10;

function edgeKey(i1: number, i2: number): string {
  return i1 < i2 ? `${i1}:${i2}` : `${i2}:${i1}`;
}

function vertexAt(positions: readonly number[], index: number): Vec2 {
  return [positions[index * 2]!, positions[index * 2 + 1]!];
}

// shouldSplitEdge over the working buffer, which projects each vertex for the bend only once.
function splitsEdgeOver(
  positions: readonly number[],
  config: DensifyConfig,
  vertex = (index: number) => vertexAt(positions, index)
): SplitsEdge {
  const bendOf = config.bend?.overVertices(positions);
  const known = new Map<string, boolean>();
  return (ia, ib) => {
    const key = edgeKey(ia, ib);
    const cached = known.get(key);
    if (cached !== undefined) return cached;
    const split =
      beyondZone(vertex(ia), vertex(ib), config.zone) ||
      (bendOf !== undefined && bendOf(ia, ib) > MAX_BEND_PX);
    known.set(key, split);
    return split;
  };
}

function edgeSplits([i0, i1, i2]: TriangleIndices, splitsEdge: SplitsEdge): EdgeSplits {
  return [splitsEdge(i0, i1), splitsEdge(i1, i2), splitsEdge(i2, i0)];
}

function emitOneSplit(tri: TriangleIndices, splits: EdgeSplits, mid: MidpointOf): number[] {
  const [i0, i1, i2] = tri;
  if (splits[0]) {
    const m = mid(i0, i1);
    return [i0, m, i2, m, i1, i2];
  }
  if (splits[1]) {
    const m = mid(i1, i2);
    return [i0, i1, m, i0, m, i2];
  }
  const m = mid(i2, i0);
  return [i0, i1, m, m, i1, i2];
}

function emitTwoSplits(tri: TriangleIndices, splits: EdgeSplits, mid: MidpointOf): number[] {
  const [i0, i1, i2] = tri;
  if (!splits[0]) {
    const m12 = mid(i1, i2);
    const m20 = mid(i2, i0);
    return [i0, i1, m20, i1, m12, m20, m20, m12, i2];
  }
  if (!splits[1]) {
    const m01 = mid(i0, i1);
    const m20 = mid(i2, i0);
    return [i0, m01, m20, m01, i1, m20, m20, i1, i2];
  }
  const m01 = mid(i0, i1);
  const m12 = mid(i1, i2);
  return [i0, m01, i2, m01, i1, m12, m01, m12, i2];
}

function emitThreeSplits(tri: TriangleIndices, mid: MidpointOf): number[] {
  const [i0, i1, i2] = tri;
  const m01 = mid(i0, i1);
  const m12 = mid(i1, i2);
  const m20 = mid(i2, i0);
  return [i0, m01, m20, m01, i1, m12, m20, m12, i2, m01, m12, m20];
}

function splitTriangle(tri: TriangleIndices, splits: EdgeSplits, mid: MidpointOf): number[] {
  const count = Number(splits[0]) + Number(splits[1]) + Number(splits[2]);
  if (count === 1) return emitOneSplit(tri, splits, mid);
  if (count === 2) return emitTwoSplits(tri, splits, mid);
  return emitThreeSplits(tri, mid);
}

// Appends midpoints to the shared working buffer so that both triangles on an edge reuse one vertex.
function midpointAppender(positions: number[]): MidpointOf {
  const known = new Map<string, number>();
  return (ia, ib) => {
    const key = edgeKey(ia, ib);
    const existing = known.get(key);
    if (existing !== undefined) return existing;
    const index = positions.length / 2;
    positions.push(
      (positions[ia * 2]! + positions[ib * 2]!) / 2,
      (positions[ia * 2 + 1]! + positions[ib * 2 + 1]!) / 2
    );
    known.set(key, index);
    return index;
  };
}

// A triangle left whole in one pass keeps edges the bend and zone rules did not split, and both
// rules depend on the edge alone, so only the triangles made in the last pass are judged again.
function refinePass(
  positions: number[],
  active: readonly RefineNode[],
  splitsEdge: SplitsEdge
): readonly RefineNode[] {
  const mid = midpointAppender(positions);
  const next: RefineNode[] = [];

  for (const node of active) {
    const splits = edgeSplits(node.tri, splitsEdge);
    if (!splits[0] && !splits[1] && !splits[2]) continue;
    const flat = splitTriangle(node.tri, splits, mid);
    for (let c = 0; c < flat.length; c += 3) {
      const child: RefineNode = { tri: [flat[c]!, flat[c + 1]!, flat[c + 2]!], children: [] };
      node.children.push(child);
      next.push(child);
    }
  }

  return next;
}

function longestInteriorEdge(
  tri: TriangleIndices,
  positions: readonly number[],
  outline: ReadonlySet<string>
): string | undefined {
  let longest: string | undefined,
    lengthSq = -Infinity;
  for (let k = 0; k < 3; k++) {
    const a = tri[k]!,
      b = tri[(k + 1) % 3]!,
      key = edgeKey(a, b);
    if (outline.has(key)) continue;
    const size =
      (positions[a * 2]! - positions[b * 2]!) ** 2 +
      (positions[a * 2 + 1]! - positions[b * 2 + 1]!) ** 2;
    if (size > lengthSq) {
      longest = key;
      lengthSq = size;
    }
  }
  return longest;
}

function splitOutline(
  tri: TriangleIndices,
  flags: EdgeSplits,
  outline: Set<string>,
  mid: MidpointOf
): void {
  for (let k = 0; k < 3; k++) {
    const a = tri[k]!,
      b = tri[(k + 1) % 3]!;
    if (!flags[k] || !outline.delete(edgeKey(a, b))) continue;
    const m = mid(a, b);
    outline.add(edgeKey(a, m));
    outline.add(edgeKey(m, b));
  }
}

// Edge refinement depends on the edge alone. Interior height probes split an interior edge (or
// add a centre if none exists), so one tile's interior never subdivides its neighbour's outline.
const settledHeightPlans = new WeakMap<Triangulation, DensifyConfig>();
export function refineHeightTriangles(input: Triangulation, config: DensifyConfig): Triangulation {
  if (settledHeightPlans.get(input) === config) return input;
  const positions = Array.from(input.positions);
  const vertices: Vec2[] = [];
  const vertex = (index: number): Vec2 => (vertices[index] ??= vertexAt(positions, index));
  const roots: RefineNode[] = [];
  const edgeCounts = new Map<string, number>();
  for (let t = 0; t < input.indices.length; t += 3) {
    const tri: TriangleIndices = [input.indices[t]!, input.indices[t + 1]!, input.indices[t + 2]!];
    roots.push({ tri, children: [] });
    for (let k = 0; k < 3; k++) {
      const key = edgeKey(tri[k]!, tri[(k + 1) % 3]!);
      edgeCounts.set(key, (edgeCounts.get(key) ?? 0) + 1);
    }
  }
  const outline = new Set([...edgeCounts].filter(([, count]) => count === 1).map(([key]) => key));
  const splitsEdge = splitsEdgeOver(positions, config, vertex);
  const mid = midpointAppender(positions);
  const checked = new Map<RefineNode, boolean>();
  let leaves: readonly RefineNode[] = roots;
  let changed = false;
  let settled = false;
  for (let pass = 0; pass < 24; pass++) {
    const requested = new Set<string>();
    const interiors = new Set<RefineNode>();
    for (const node of leaves) {
      const flags = edgeSplits(node.tri, splitsEdge);
      for (let k = 0; k < 3; k++)
        if (flags[k]) requested.add(edgeKey(node.tri[k]!, node.tri[(k + 1) % 3]!));
      let need = checked.get(node);
      if (need === undefined) {
        const [a, b, c] = node.tri;
        need = config.surface!.needsRefinement(vertex(a), vertex(b), vertex(c));
        checked.set(node, need);
      }
      if (need) {
        const longest = longestInteriorEdge(node.tri, positions, outline);
        if (longest) requested.add(longest);
        else interiors.add(node);
      }
    }
    if (!requested.size && !interiors.size) {
      settled = true;
      break;
    }
    changed = true;
    const next: RefineNode[] = [];
    for (const node of leaves) {
      const flags = edgeSplits(node.tri, (a, b) => requested.has(edgeKey(a, b)));
      if (!flags.some(Boolean) && !interiors.has(node)) {
        next.push(node);
        continue;
      }
      let flat: number[];
      if (flags.some(Boolean)) {
        splitOutline(node.tri, flags, outline, mid);
        flat = splitTriangle(node.tri, flags, mid);
      } else {
        const [a, b, c] = node.tri,
          centre = positions.length / 2;
        positions.push(
          (positions[a * 2]! + positions[b * 2]! + positions[c * 2]!) / 3,
          (positions[a * 2 + 1]! + positions[b * 2 + 1]! + positions[c * 2 + 1]!) / 3
        );
        flat = [a, b, centre, b, c, centre, c, a, centre];
      }
      for (let c = 0; c < flat.length; c += 3) {
        const child: RefineNode = { tri: [flat[c]!, flat[c + 1]!, flat[c + 2]!], children: [] };
        node.children.push(child);
        next.push(child);
      }
    }
    leaves = next;
  }
  const result = changed
    ? {
        positions: Float64Array.from(positions),
        indices: Uint32Array.from(appendLeaves(roots, [])),
      }
    : input;
  // Restoring an already-present outline returns this same immutable plan. It needs no second
  // full height pass; changed stations or a pass-limit exit still receive every check.
  if (settled) settledHeightPlans.set(result, config);
  return result;
}

function appendLeaves(nodes: readonly RefineNode[], out: number[]): number[] {
  for (const node of nodes) {
    if (node.children.length === 0) out.push(node.tri[0], node.tri[1], node.tri[2]);
    else appendLeaves(node.children, out);
  }
  return out;
}

function refineLongEdges(input: Triangulation, config: DensifyConfig): Triangulation {
  const positions = Array.from(input.positions);
  const roots: RefineNode[] = [];
  for (let t = 0; t < input.indices.length; t += 3) {
    roots.push({
      tri: [input.indices[t]!, input.indices[t + 1]!, input.indices[t + 2]!],
      children: [],
    });
  }

  const splitsEdge = splitsEdgeOver(positions, config);
  let active: readonly RefineNode[] = roots;
  for (let pass = 0; pass < MAX_REFINE_PASSES && active.length > 0; pass++) {
    active = refinePass(positions, active, splitsEdge);
  }

  return {
    positions: Float64Array.from(positions),
    indices: Uint32Array.from(appendLeaves(roots, [])),
  };
}

// A degenerate triangle refined splits into more of them, so the triangulation loses its own first.
// Halving the sides of a triangle with a sharp corner leaves its tip ever thinner, so each refinement
// loses the ones it made.
export function refineTriangles(
  input: Triangulation,
  config: DensifyConfig | undefined
): Triangulation {
  if (!config) return input;
  const sound = withoutDegenerates(input);
  // Height fragments share deterministic split boundary stations. Mending them after the
  // adaptive pass could move one owner’s outline without moving its neighbour’s.
  const wedged = config.surface
    ? refineHeightTriangles(sound, config)
    : withoutDegenerates(refineLongEdges(sound, config));
  return config.boundaries
    ? withoutDegenerates(refineLabelBoundaries(wedged, config.boundaries))
    : wedged;
}
